import { Link } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { useConversations } from "@/hooks/useApiData";

export const Messages = () => {
  const { items: CONVERSATIONS, loading, error } = useConversations();

  return (
    <div data-testid="messages-root">
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:text-xs">
        Account · Messages
      </p>
      <h1 className="mt-3 font-display text-4xl font-medium leading-[0.95] tracking-tight md:mt-4 md:text-6xl">
        Your <span className="italic">threads</span>.
      </h1>
      <p className="mt-4 max-w-xl text-sm text-muted-foreground md:mt-5 md:text-base">
        Every confirmed booking gets its own conversation with the host. Messaging
        closes 24 hours after check-out.
      </p>

      {loading ? (
        <div className="mt-10 divide-y divide-foreground/10 border-y border-foreground/10 md:mt-14">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="grid animate-pulse grid-cols-[64px_1fr_auto] items-center gap-4 py-5">
              <div className="h-14 w-16 bg-muted" />
              <div><div className="h-5 w-48 bg-muted" /><div className="mt-2 h-3 w-64 bg-muted" /><div className="mt-3 h-4 w-3/4 bg-muted" /></div>
              <div className="h-3 w-16 bg-muted" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="mt-10 border border-dashed border-foreground/20 px-6 py-14 text-center md:mt-14">
          <p className="font-display text-3xl">Messages unavailable.</p>
          <p className="mt-2 text-sm text-muted-foreground">We could not load your conversations. Refresh the page to try again.</p>
        </div>
      ) : CONVERSATIONS.length === 0 ? (
        <div className="mt-10 border border-dashed border-foreground/20 px-6 py-14 text-center md:mt-14">
          <MessageSquare className="mx-auto h-7 w-7 text-muted-foreground" />
          <p className="mt-4 font-display text-3xl">No conversations yet.</p>
          <p className="mt-2 text-sm text-muted-foreground">Messages with hosts will appear after you have a confirmed booking.</p>
        </div>
      ) : (
        <ul className="mt-10 divide-y divide-foreground/10 border-y border-foreground/10 md:mt-14">
          {CONVERSATIONS.map((c) => (
            <li key={c.bookingId}>
              <Link
                to={`/account/bookings/${c.bookingId}/messages`}
                data-testid={`conversation-${c.bookingId}`}
                className="group grid grid-cols-[64px_1fr_auto] items-center gap-4 py-5 transition-colors hover:bg-muted/30"
              >
                <img src={c.property.images[0]} alt="" className="h-14 w-16 object-cover" />
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <p className="truncate font-display text-lg font-medium tracking-tight md:text-xl">{c.property.name}</p>
                    {!c.open && <span className="rounded-full bg-muted px-2 py-0.5 text-[9px] font-medium uppercase tracking-[0.15em] text-muted-foreground">Closed</span>}
                  </div>
                  <p className="mt-1 truncate text-[11px] uppercase tracking-[0.15em] text-muted-foreground">{c.host.name} · {c.stayDates}</p>
                  <p className="mt-2 line-clamp-1 text-sm text-muted-foreground">{c.lastMessage}</p>
                </div>
                <div className="flex flex-col items-end gap-2 text-right">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{c.lastTimestamp}</p>
                  {c.unread > 0 && <span data-testid={`unread-${c.bookingId}`} className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-2 text-[10px] font-medium text-primary-foreground">{c.unread}</span>}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Messages;
